import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, defineProperty as _defineProperty, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { ClassNames } from '@emotion/react';
import React, { useEffect, useMemo, useState, useRef, useCallback, useImperativeHandle } from 'react';
import { isFragment } from 'react-is';
import { classes } from './classes.esm.js';
import useCallbackRef from '../../../hooks/use-callback-ref.esm.js';
import useRefresh from '../../../hooks/use-refresh.esm.js';
import useTimeout from '../../../hooks/use-timeout.esm.js';
import createComponent from '../../../styles-system/create-component.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';
import { defaultBreakpoints } from '../../../themes/theme-keys/breakpoints.esm.js';
import animate from '../../../utils/animate.esm.js';
import { isNumber } from '../../../utils/assertion.esm.js';
import debounce from '../../../utils/debounce.esm.js';
import { get, EMPTY_OBJECT } from '../../../utils/object.esm.js';
import ownerWindow from '../../../utils/owner-window.esm.js';
import { getNormalizedScrollLeft, detectScrollType } from '../../../utils/scroll-left.esm.js';
import { useDrawerContext } from '../../_layouts/drawer/drawer-context.esm.js';
import Pane from '../../_layouts/pane/pane.esm.js';
import Box from '../../_primitives/box/box.esm.js';
import Divider from '../../_primitives/divider/divider.esm.js';
import Flex from '../../_primitives/flex/flex.esm.js';
import ScrollbarSizeListener from '../../_utils/scrollbar-size-listener.esm.js';
import ArrowIndicator from '../arrow-indicator.esm.js';
import MoreTab from '../more-tab.esm.js';
import TabIndicator from '../tab-indicator.esm.js';
import TabScrollButton from '../tab-scroll-button.esm.js';
import { getTabsMeta, getTabMeta } from '../tab-utils.esm.js';
import { useTabsContext } from '../tabs-context.esm.js';
import { TAB_THEME_KEY } from '../types.esm.js';
import isReact from '../../../utils/is-react.esm.js';

var _excluded = ["aria-label", "aria-labelledby", "action", "centered", "children", "className", "as", "indicatorColor", "onChange", "orientation", "ScrollButtonComponent", "scrollButtons", "scrollButtonsBreakpoints", "selectionFollowsFocus", "TabIndicatorProps", "TabScrollButtonProps", "value", "mode", "direction", "variant", "moreLabel", "divider", "spacing", "indicatorType", "indicatorPosition", "morePaneProps", "tabsWrapperProps", "spaceBetweenMore", "hideIndicator", "disableAnimation", "indicatorLength", "buttonList", "renderElement"];
var DEFAULT_TABS_TAG = 'div';
var CsgTabs = createComponent(DEFAULT_TABS_TAG, 'csg-tabs');
var moreFauxTabsSx = {
  flex: '1 1 auto',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  width: '100%'
};
var Tabs = /*#__PURE__*/React.forwardRef(function Tabs(props, ref) {
  var _useDrawerContext;
  var ariaLabel = props['aria-label'],
    ariaLabelledBy = props['aria-labelledby'],
    action = props.action,
    _props$centered = props.centered,
    centered = _props$centered === void 0 ? false : _props$centered,
    childrenProp = props.children,
    className = props.className,
    _props$as = props.as,
    component = _props$as === void 0 ? DEFAULT_TABS_TAG : _props$as,
    _props$indicatorColor = props.indicatorColor,
    indicatorColor = _props$indicatorColor === void 0 ? 'secondary' : _props$indicatorColor,
    onChange = props.onChange,
    _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? 'horizontal' : _props$orientation,
    _props$ScrollButtonCo = props.ScrollButtonComponent,
    ScrollButtonComponent = _props$ScrollButtonCo === void 0 ? TabScrollButton : _props$ScrollButtonCo,
    _props$scrollButtons = props.scrollButtons,
    scrollButtons = _props$scrollButtons === void 0 ? 'auto' : _props$scrollButtons,
    scrollButtonsBreakpoints = props.scrollButtonsBreakpoints,
    selectionFollowsFocus = props.selectionFollowsFocus,
    _props$TabIndicatorPr = props.TabIndicatorProps,
    TabIndicatorProps = _props$TabIndicatorPr === void 0 ? {} : _props$TabIndicatorPr,
    TabScrollButtonProps = props.TabScrollButtonProps,
    valueProps = props.value,
    _props$mode = props.mode,
    mode = _props$mode === void 0 ? 'standard' : _props$mode,
    _props$direction = props.direction,
    direction = _props$direction === void 0 ? 'ltr' : _props$direction,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'tabs' : _props$variant,
    _props$moreLabel = props.moreLabel,
    moreLabel = _props$moreLabel === void 0 ? 'More' : _props$moreLabel,
    divider = props.divider,
    _props$spacing = props.spacing,
    spacing = _props$spacing === void 0 ? 'md' : _props$spacing,
    _props$indicatorType = props.indicatorType,
    indicatorType = _props$indicatorType === void 0 ? 'bar' : _props$indicatorType,
    indicatorPosition = props.indicatorPosition,
    _props$morePaneProps = props.morePaneProps,
    morePaneProps = _props$morePaneProps === void 0 ? EMPTY_OBJECT : _props$morePaneProps,
    _props$tabsWrapperPro = props.tabsWrapperProps,
    tabsWrapperProps = _props$tabsWrapperPro === void 0 ? EMPTY_OBJECT : _props$tabsWrapperPro,
    spaceBetweenMore = props.spaceBetweenMore,
    _props$hideIndicator = props.hideIndicator,
    hideIndicator = _props$hideIndicator === void 0 ? false : _props$hideIndicator,
    disableAnimation = props.disableAnimation,
    _props$indicatorLengt = props.indicatorLength,
    indicatorLengthProp = _props$indicatorLengt === void 0 ? 1 : _props$indicatorLengt,
    buttonList = props.buttonList,
    renderElement = props.renderElement,
    other = _objectWithoutProperties(props, _excluded);
  var _useTabsContext = useTabsContext(),
    tabVariant = _useTabsContext.variant,
    selectionChangedObserver = _useTabsContext.selectionChangedObserver,
    selection = _useTabsContext.selection;
  var value = selection !== undefined ? selection : valueProps;
  var theme = useSafeThemeUI(props);
  var breakpoints = get(theme, 'breakpoints', defaultBreakpoints);
  var scrollable = mode === 'scrollable';
  var isRtl = direction === 'rtl' || theme.direction === 'rtl';
  var vertical = orientation === 'vertical';
  var scrollStart = vertical ? 'scrollTop' : 'scrollLeft';
  var start = vertical ? 'top' : 'left';
  var end = vertical ? 'bottom' : 'right';
  var clientSize = vertical ? 'clientHeight' : 'clientWidth';
  var size = vertical ? 'height' : 'width';
  var refresh = useRefresh();
  var delayExecute = useTimeout(150);
  // delay refresh on mount to update indicator position, first render will not get the positioning
  useEffect(function () {
    return delayExecute(refresh);
  }, [refresh, delayExecute]);
  var _ref = (_useDrawerContext = useDrawerContext()) !== null && _useDrawerContext !== void 0 ? _useDrawerContext : {},
    onEnteredObserver = _ref.onEnteredObserver;
  useEffect(function () {
    var handelOnEntered = function handelOnEntered() {
      delayExecute(refresh);
    };
    onEnteredObserver === null || onEnteredObserver === void 0 ? void 0 : onEnteredObserver.attach(handelOnEntered);
    return function () {
      return onEnteredObserver === null || onEnteredObserver === void 0 ? void 0 : onEnteredObserver.detach(handelOnEntered);
    };
  }, [delayExecute, onEnteredObserver, refresh]);
  if (process.env.NODE_ENV !== 'production') {
    if (centered && scrollable) {
      console.error('Tui: You can not use the `centered={true}` and `mode="scrollable"` properties ' + 'at the same time on a `Tabs` component.');
    }
  }
  var Component = useMemo(function () {
    return component === DEFAULT_TABS_TAG ? CsgTabs : createComponent(component, 'csg-tabs');
  }, [component]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    mounted = _useState2[0],
    setMounted = _useState2[1];
  var refreshCallback = useRefresh();
  var _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    indicatorStyle = _useState4[0],
    setIndicatorStyle = _useState4[1];
  var _useState5 = useState({
      start: false,
      end: false
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    displayScroll = _useState6[0],
    setDisplayScroll = _useState6[1];
  var styleOverrides = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, indicatorStyle), TabIndicatorProps.style);
  }, [indicatorStyle, TabIndicatorProps.style]);
  var _useState7 = useState({}),
    _useState8 = _slicedToArray(_useState7, 2),
    scrollerStyle = _useState8[0],
    setScrollerStyle = _useState8[1];
  var valueToIndex = new Map();
  var moreIncludesRef = useRef([]);
  var tabsRef = useRef(null);
  var tabListRef = useRef(null);
  var fauxTabsRef = useRef(null);
  var fauxTabListRef = useRef(null);
  var handleChange = useCallback(function (event, source) {
    onChange && onChange(event, source);
    selectionChangedObserver === null || selectionChangedObserver === void 0 ? void 0 : selectionChangedObserver.notify(_objectSpread2(_objectSpread2({}, source), {}, {
      source: 'tabs'
    }));
  }, [onChange, selectionChangedObserver]);
  var updateIndicatorState = useCallbackRef(function () {
    var tabsMeta = getTabsMeta(tabsRef.current, tabsRef.current ? getNormalizedScrollLeft(tabsRef.current, direction || theme.direction) : 0);
    var tabMeta = getTabMeta(orientation, tabListRef.current, valueToIndex, value, mode === 'more' ? moreIncludesRef.current.map(function (i) {
      return i.value;
    }) : undefined, hideIndicator);
    var startValue = 0;
    if (tabMeta && tabsMeta) {
      if (vertical) {
        startValue = tabMeta.top - tabsMeta.top + tabsMeta.scrollTop;
      } else {
        var correction = isRtl ? tabsMeta.scrollLeftNormalized + tabsMeta.clientWidth - tabsMeta.scrollWidth : tabsMeta.scrollLeft;
        startValue = tabMeta.left - tabsMeta.left + correction + (indicatorType === 'arrow' ? Math.floor(tabMeta.width / 2) : 0);
      }
    }
    var indicatorLength = tabMeta ? tabMeta[size] : 0;
    if (indicatorType === 'bar' && indicatorLengthProp > 0 && indicatorLengthProp < 1) {
      var adjustedLength = indicatorLengthProp * indicatorLength;
      startValue = startValue + (indicatorLength - adjustedLength) / 2;
      indicatorLength = adjustedLength;
    }
    var newIndicatorStyle = _objectSpread2(_defineProperty({}, start, startValue), indicatorType === 'bar' ? _defineProperty({}, size, indicatorLength) : {});

    // IE 11 support, replace with Number.isNaN
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(indicatorStyle[start]) || isNaN(indicatorStyle[size])) {
      setIndicatorStyle(function (prev) {
        return JSON.stringify(prev) !== JSON.stringify(newIndicatorStyle) ? newIndicatorStyle : prev;
      });
    } else {
      var dStart = Math.abs(indicatorStyle[start] - newIndicatorStyle[start]);
      var dSize = Math.abs(indicatorStyle[size] - newIndicatorStyle[size]);
      if (dStart >= 1 || dSize >= 1) {
        setIndicatorStyle(newIndicatorStyle);
      }
    }
  });
  var scroll = useCallback(function (scrollValue) {
    animate(scrollStart, tabsRef.current, scrollValue);
  }, [scrollStart]);
  var moveTabsScroll = useCallback(function (delta) {
    var scrollValue = tabsRef.current ? tabsRef.current[scrollStart] : 0;
    if (vertical) {
      scrollValue += delta;
    } else {
      scrollValue += delta * (isRtl ? -1 : 1);
      // Fix for Edge
      scrollValue *= isRtl && detectScrollType() === 'reverse' ? -1 : 1;
    }
    scroll(scrollValue);
  }, [isRtl, scroll, scrollStart, vertical]);
  var handleStartScrollClick = useCallback(function () {
    tabsRef.current && moveTabsScroll(-tabsRef.current[clientSize]);
  }, [clientSize, moveTabsScroll]);
  var handleEndScrollClick = useCallback(function () {
    tabsRef.current && moveTabsScroll(tabsRef.current[clientSize]);
  }, [clientSize, moveTabsScroll]);
  var handleScrollbarSizeChange = useCallback(function (size) {
    var scrollbarHeight = size === null || size === void 0 ? void 0 : size.height;
    if (isNumber(scrollbarHeight)) {
      setScrollerStyle({
        marginBottom: -scrollbarHeight
      });
    }
  }, []);
  var scrollSelectedIntoView = useCallbackRef(function () {
    var tabsMeta = getTabsMeta(tabsRef.current, tabsRef.current ? getNormalizedScrollLeft(tabsRef.current, direction || theme.direction) : 0);
    var tabMeta = getTabMeta(orientation, tabListRef.current, valueToIndex, value, mode === 'more' ? moreIncludesRef.current.map(function (i) {
      return i.value;
    }) : undefined, hideIndicator);
    if (!tabMeta || !tabsMeta) {
      return;
    }
    if (tabMeta[start] < tabsMeta[start]) {
      // left side of button is out of view
      var nextScrollStart = tabsMeta[scrollStart] + (tabMeta[start] - tabsMeta[start]);
      scroll(nextScrollStart);
    } else if (tabMeta[end] > tabsMeta[end]) {
      // right side of button is out of view
      var _nextScrollStart = tabsMeta[scrollStart] + (tabMeta[end] - tabsMeta[end]);
      scroll(_nextScrollStart);
    }
  });
  var moreModeRefreshOnResize = useCallbackRef(function () {
    if (mode === 'more') {
      refreshCallback();
    }
  });
  var updateScrollButtonState = useCallbackRef(function () {
    if (tabsRef.current && scrollable && scrollButtons !== 'off') {
      var _tabsRef$current = tabsRef.current,
        scrollTop = _tabsRef$current.scrollTop,
        scrollHeight = _tabsRef$current.scrollHeight,
        clientHeight = _tabsRef$current.clientHeight,
        scrollWidth = _tabsRef$current.scrollWidth,
        clientWidth = _tabsRef$current.clientWidth;
      var showStartScroll;
      var showEndScroll;
      if (vertical) {
        showStartScroll = scrollTop > 1;
        showEndScroll = scrollTop < scrollHeight - clientHeight - 1;
      } else {
        var scrollLeft = getNormalizedScrollLeft(tabsRef.current, direction || theme.direction);
        // use 1 for the potential rounding error with browser zooms.
        showStartScroll = isRtl ? scrollLeft < scrollWidth - clientWidth - 1 : scrollLeft > 1;
        showEndScroll = !isRtl ? scrollLeft < scrollWidth - clientWidth - 1 : scrollLeft > 1;
      }
      if (showStartScroll !== displayScroll.start || showEndScroll !== displayScroll.end) {
        setDisplayScroll({
          start: showStartScroll,
          end: showEndScroll
        });
      }
    }
  });
  useEffect(function () {
    var handleResize = debounce(function () {
      updateIndicatorState();
      updateScrollButtonState();
      moreModeRefreshOnResize();
    });
    var win = ownerWindow(tabsRef.current);
    win.addEventListener('resize', handleResize);
    return function () {
      handleResize.clear();
      win.removeEventListener('resize', handleResize);
    };
  }, [updateIndicatorState, updateScrollButtonState, moreModeRefreshOnResize]);
  var handleTabsScroll = useMemo(function () {
    return debounce(function () {
      updateScrollButtonState();
    });
  }, [updateScrollButtonState]);
  useEffect(function () {
    return function () {
      handleTabsScroll.clear();
    };
  }, [handleTabsScroll]);
  useEffect(function () {
    setMounted(true);
  }, []);
  useEffect(function () {
    updateIndicatorState();
    updateScrollButtonState();
  });
  useEffect(function () {
    scrollSelectedIntoView();
  }, [scrollSelectedIntoView, indicatorStyle]);
  useImperativeHandle(action, function () {
    return {
      updateIndicator: updateIndicatorState,
      updateScrollButtons: updateScrollButtonState
    };
  }, [updateIndicatorState, updateScrollButtonState]);
  var indicator = /*#__PURE__*/React.createElement(ClassNames, null, function (_ref3) {
    var css = _ref3.css;
    return indicatorType === 'bar' ? /*#__PURE__*/React.createElement(TabIndicator, _extends({
      className: css(classes.indicator),
      orientation: orientation,
      indicatorPosition: indicatorPosition,
      color: indicatorColor,
      disableAnimation: disableAnimation
    }, TabIndicatorProps, {
      style: styleOverrides
    })) : /*#__PURE__*/React.createElement(ArrowIndicator, _extends({
      className: css(classes.indicator),
      orientation: orientation,
      indicatorPosition: indicatorPosition,
      color: indicatorColor
    }, TabIndicatorProps, {
      style: styleOverrides
    }));
  });
  var childIndex = 0;
  var tabIndex = 0;
  var childrenCount = React.Children.count(childrenProp);
  var children = [];
  var moreChildren = [];
  moreIncludesRef.current = [];
  var fauxChildren = [];
  var fauxTabsMeta = getTabsMeta(fauxTabsRef.current, fauxTabsRef.current ? getNormalizedScrollLeft(fauxTabsRef.current, direction || theme.direction) : 0);
  var shouldAddToMore = function shouldAddToMore(value) {
    var tabMeta = getTabMeta('horizontal', fauxTabListRef.current, valueToIndex, value, undefined, hideIndicator);
    return fauxTabsMeta && tabMeta && tabMeta.right + tabMeta.width * 2.2 > fauxTabsMeta.right;
  };
  var MoreTabComponent = null;
  React.Children.map(childrenProp, function (child) {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    if (process.env.NODE_ENV !== 'production') {
      if (isFragment(child)) {
        console.error(["Tui: The Tabs component doesn't accept a Fragment as a child.", 'Consider providing an array instead.'].join('\n'));
      }
    }
    var childValue = child.props.value === undefined ? childIndex : child.props.value;
    valueToIndex.set(childValue, childIndex);
    var selected = childValue === value;
    var childLabel = typeof child.props.children === 'string' ? child.props.children : child.props.label ? child.props.label : '';
    var _shouldAddToMore = mode === 'more' && shouldAddToMore(childValue);
    var cloneChild = /*#__PURE__*/React.cloneElement(child, _objectSpread2({
      selected: selected,
      selectionfollowfocus: selectionFollowsFocus,
      onChange: handleChange,
      value: childValue,
      orientation: orientation,
      indicatorposition: indicatorPosition,
      label: childLabel
    }, _shouldAddToMore ? {
      variant: 'moreTab'
    } : {}));
    // const more = true
    if (_shouldAddToMore) {
      moreChildren.push(cloneChild);
      if (divider && (tabIndex < childrenCount - 1 || moreChildren.length > 0 && !spaceBetweenMore)) {
        moreChildren.push(/*#__PURE__*/React.createElement(Divider, {
          width: "100%"
        }));
      }
      moreIncludesRef.current.push({
        value: childValue,
        label: childLabel
      });
    } else if (divider) {
      children.push(cloneChild);
      if (tabIndex < childrenCount - 1) {
        isReact.element(divider) ? children.push(
        /*#__PURE__*/
        //@ts-ignore
        React.cloneElement(divider, _objectSpread2({
          key: "tab-divider-".concat(childIndex)
        }, divider.props))) : children.push(divider);
      }
    } else {
      children.push(cloneChild);
    }
    if (mode === 'more') {
      var cloneFauxChild = /*#__PURE__*/React.cloneElement(child, {
        value: childValue,
        orientation: orientation,
        indicatorPosition: indicatorPosition,
        label: childLabel,
        'automation-id': undefined // remove automation-id from this set of clones
      });
      if (divider) {
        fauxChildren.push(cloneFauxChild);
        if (tabIndex < childrenCount - 1) {
          isReact.element(divider) ? fauxChildren.push(
          /*#__PURE__*/
          //@ts-ignore
          React.cloneElement(divider, _objectSpread2({
            key: "faux-tab-divider-".concat(childIndex)
          }, divider.props))) : fauxChildren.push(divider);
        }
      } else {
        fauxChildren.push(cloneFauxChild);
      }
    }
    childIndex += divider ? 2 : 1;
    tabIndex += 1;
    return null;
  });
  if (moreChildren.length > 0) {
    var moreTab = /*#__PURE__*/React.createElement(MoreTab, {
      key: "tabs-more-tab",
      includeValues: moreIncludesRef.current,
      placeholder: moreLabel,
      paneProps: morePaneProps,
      buttonList: buttonList
    }, moreChildren);
    if (spaceBetweenMore) {
      MoreTabComponent = moreTab;
    } else {
      children.push(moreTab);
    }
  }
  var getConditionalElements = function getConditionalElements() {
    var conditionalElements = {};
    conditionalElements.scrollbarSizeListener = scrollable ? /*#__PURE__*/React.createElement(ClassNames, null, function (_ref4) {
      var _cx;
      var css = _ref4.css,
        cx = _ref4.cx;
      return /*#__PURE__*/React.createElement(ScrollbarSizeListener, {
        className: cx((_cx = {
          'csg-tui-tabs-scrollbar-listen': true
        }, _defineProperty(_cx, css(classes.scrollable), true), _defineProperty(_cx, css(classes.scrollableHorizontal), !vertical), _defineProperty(_cx, css(classes.scrollableVertical), vertical), _cx)),
        onChange: handleScrollbarSizeChange
      });
    }) : null;
    var scrollButtonsActive = displayScroll.start || displayScroll.end;
    var showScrollButtons = scrollable && (scrollButtons === 'auto' && scrollButtonsActive || scrollButtons === 'desktop' || scrollButtons === 'on');
    conditionalElements.scrollButtonStart = showScrollButtons ? /*#__PURE__*/React.createElement(ClassNames, null, function (_ref5) {
      var _cx2;
      var css = _ref5.css,
        cx = _ref5.cx;
      return /*#__PURE__*/React.createElement(ScrollButtonComponent, _extends({
        orientation: orientation,
        direction: isRtl ? 'right' : 'left',
        onClick: handleStartScrollClick,
        disabled: !displayScroll.start,
        className: cx((_cx2 = {}, _defineProperty(_cx2, "csg-tui-tabs-scroll-button-".concat(orientation === 'horizontal' ? isRtl ? 'right' : 'left' : 'top'), true), _defineProperty(_cx2, css(classes.scrollButtonsDesktop(breakpoints, scrollButtonsBreakpoints)), !!scrollButtonsBreakpoints && scrollButtons !== 'on'), _cx2))
      }, TabScrollButtonProps));
    }) : null;
    conditionalElements.scrollButtonEnd = showScrollButtons ? /*#__PURE__*/React.createElement(ClassNames, null, function (_ref6) {
      var _cx3;
      var css = _ref6.css,
        cx = _ref6.cx;
      return /*#__PURE__*/React.createElement(ScrollButtonComponent, _extends({
        orientation: orientation,
        direction: isRtl ? 'left' : 'right',
        onClick: handleEndScrollClick,
        disabled: !displayScroll.end,
        className: cx((_cx3 = {}, _defineProperty(_cx3, "csg-tui-tabs-scroll-button-".concat(orientation === 'horizontal' ? isRtl ? 'left' : 'right' : 'top'), true), _defineProperty(_cx3, css(classes.scrollButtonsDesktop(breakpoints, scrollButtonsBreakpoints)), !!scrollButtonsBreakpoints && scrollButtons !== 'on'), _cx3))
      }, TabScrollButtonProps));
    }) : null;
    conditionalElements.moreFauxTabs = mode === 'more' ? /*#__PURE__*/React.createElement(Box, {
      ref: fauxTabsRef,
      absolute: true,
      top: "99999px",
      sx: moreFauxTabsSx
    }, /*#__PURE__*/React.createElement(Flex, {
      ref: fauxTabListRef,
      flexWrap: 'nowrap',
      gap: spacing
    }, fauxChildren)) : null;
    return conditionalElements;
  };
  var handleKeyDown = useCallback(function (event) {
    var _tabListRef$current, _tabListRef$current2, _tabListRef$current3, _tabListRef$current4;
    var target = event.target;
    // Keyboard navigation assumes that [role="tab"] are siblings
    // though we might warn in the future about nested, interactive elements
    // as a a11y violation
    var role = target.getAttribute('role');
    if (role !== 'tab') {
      return;
    }
    var newFocusTarget = null;
    var previousItemKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    var nextItemKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    if (orientation === 'horizontal' && (direction === 'rtl' || theme.direction === 'rtl')) {
      // swap previousItemKey with nextItemKey
      previousItemKey = 'ArrowRight';
      nextItemKey = 'ArrowLeft';
    }
    switch (event.key) {
      case previousItemKey:
        newFocusTarget = target.previousElementSibling || (tabListRef === null || tabListRef === void 0 ? void 0 : (_tabListRef$current = tabListRef.current) === null || _tabListRef$current === void 0 ? void 0 : _tabListRef$current.lastChild);
        break;
      case nextItemKey:
        newFocusTarget = target.nextElementSibling || (tabListRef === null || tabListRef === void 0 ? void 0 : (_tabListRef$current2 = tabListRef.current) === null || _tabListRef$current2 === void 0 ? void 0 : _tabListRef$current2.firstChild);
        break;
      case 'Home':
        newFocusTarget = tabListRef === null || tabListRef === void 0 ? void 0 : (_tabListRef$current3 = tabListRef.current) === null || _tabListRef$current3 === void 0 ? void 0 : _tabListRef$current3.firstChild;
        break;
      case 'End':
        newFocusTarget = tabListRef === null || tabListRef === void 0 ? void 0 : (_tabListRef$current4 = tabListRef.current) === null || _tabListRef$current4 === void 0 ? void 0 : _tabListRef$current4.lastChild;
        break;
    }
    if (newFocusTarget !== null) {
      newFocusTarget.focus();
      event.preventDefault();
    }
  }, [direction, orientation, theme.direction]);
  var conditionalElements = getConditionalElements();
  // eslint-disable-next-line no-console
  // console.log(`tabs render value ${value}`)

  function tabsList(props) {
    var css = props.css,
      cx = props.cx;
    return /*#__PURE__*/React.createElement(Flex, {
      gap: spacing,
      flexWrap: "nowrap",
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      flexDirection: vertical ? 'column' : 'row',
      alignItems: vertical ? 'flex-start' : 'center',
      className: cx(_defineProperty({
        'csg-tui-tab-list': true
      }, css(classes.centered), centered && !scrollable)),
      onKeyDown: handleKeyDown,
      ref: tabListRef,
      role: "tablist",
      height: "100%"
    }, children);
  }
  return /*#__PURE__*/React.createElement(ClassNames, null, function (classNameProps) {
    var _cx5, _cx6, _cx7;
    var css = classNameProps.css,
      cx = classNameProps.cx;
    return /*#__PURE__*/React.createElement(Pane, _extends({
      className: cx((_cx5 = {
        'csg-tui-tabs-wrapper': true
      }, _defineProperty(_cx5, css(classes.vertical), vertical), _defineProperty(_cx5, css(classes.verticalNavTabsWrapper), tabVariant === 'verticalNav'), _cx5)),
      borderRadius: "none",
      border: false,
      bg: "transparent",
      overflow: "hidden"
    }, tabsWrapperProps), renderElement && renderElement(), /*#__PURE__*/React.createElement(Component, _extends({
      className: cx((_cx6 = {
        'csg-tui-tabs': true
      }, _defineProperty(_cx6, css(classes.root), true), _defineProperty(_cx6, css(classes.vertical), vertical), _cx6), className),
      ref: ref,
      variant: tabVariant ? "".concat(tabVariant, ".").concat(variant) : variant,
      __themeKey: TAB_THEME_KEY
    }, other), conditionalElements.scrollButtonStart, conditionalElements.scrollbarSizeListener, /*#__PURE__*/React.createElement(Box, {
      className: cx((_cx7 = {
        'csg-tui-tabs-scroller': true
      }, _defineProperty(_cx7, css(classes.scroller), true), _defineProperty(_cx7, css(classes.fixed), !scrollable), _defineProperty(_cx7, css(classes.scrollable), scrollable), _defineProperty(_cx7, css(classes.scrollableHorizontal), scrollable && !vertical), _defineProperty(_cx7, css(classes.scrollableVertical), scrollable && vertical), _cx7)),
      style: scrollerStyle,
      ref: tabsRef,
      onScroll: handleTabsScroll
    }, MoreTabComponent ? /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "space-between",
      flexWrap: "nowrap"
    }, tabsList(classNameProps), MoreTabComponent) : tabsList(classNameProps), mounted && !hideIndicator && indicator), conditionalElements.scrollButtonEnd, conditionalElements.moreFauxTabs));
  });
});

export { Tabs };
//# sourceMappingURL=tabs.esm.js.map
